import { useEffect } from 'react'
import { useSwiper } from 'swiper/react'
import { twMerge } from 'tailwind-merge'
import { dayOfWeekJP } from '~/lib/calendar/calendar'
import { useNow } from '~/lib/date/NowProvider'
import { getDate, getMonth, getYear } from '~/lib/date/date'
import { dateTimeToDateText } from '~/lib/date/dateTime'
import type { InventoryCalendarItemFragment } from '~/lib/graphql/graphql'
import type { DateText } from '~/lib/graphql/scalars'
import { money } from '~/lib/money/money'
import { ScrollButton } from '~/ui/IconButton'

type Props = {
  inventoryCalendar: InventoryCalendarItemFragment[]
  selectedDate?: DateText
  isFirst?: boolean
  isLast?: boolean
  onSelected: (date: DateText) => void
  slideIndex: number
  isSeatOnly: boolean
}
const InventoryCalendar: React.FC<Props> = ({
  inventoryCalendar,
  isFirst,
  isLast,
  onSelected,
  selectedDate,
  slideIndex,
  isSeatOnly,
}) => {
  const now = useNow()
  const swiper = useSwiper()
  const nextSlide = () => {
    swiper.slideNext()
  }

  const prevSlide = () => {
    swiper.slidePrev()
  }

  const handleDateClick = (calendar: InventoryCalendarItemFragment, disabled: boolean) => {
    if (disabled || calendar.inventory.length === 0) {
      return
    }
    onSelected(calendar.date.date)
  }

  //選択日付がある場合、当該日付があるSlideを表示する
  useEffect(() => {
    for (const calendar of inventoryCalendar) {
      if (selectedDate === calendar.date.date) {
        swiper.slideTo(slideIndex, 1)
      }
    }
  }, [inventoryCalendar, selectedDate, slideIndex, swiper])

  return (
    <>
      <div className="flex items-center justify-between">
        <ScrollButton
          direction="left"
          onClick={prevSlide}
          disabled={isFirst}
          ariaLabel="前月のカレンダーを見る"
        />
        <h2 className="text-center text-lg font-bold">
          {getYear(inventoryCalendar[0].date.date)}年 {getMonth(inventoryCalendar[0].date.date)}月
        </h2>
        <ScrollButton
          direction="right"
          onClick={nextSlide}
          disabled={isLast}
          ariaLabel="翌月のカレンダーを見る"
        />
      </div>
      <div className="mt-2 grid w-full grid-cols-7 items-center justify-between">
        {/* 曜日を出力 */}
        {Object.entries(dayOfWeekJP).map((dayOfWeek, index) => {
          return (
            <div
              key={index}
              className={twMerge(
                'py-2 text-center',
                dayOfWeek[0] === 'SUN' ? ' text-peach-dark' : '',
                dayOfWeek[0] === 'SAT' ? 'text-blueberry-dark' : ''
              )}
            >
              {dayOfWeek[1]}
            </div>
          )
        })}
        {/* カレンダーの日付と在庫を出力 */}
        {inventoryCalendar.map((date, index) => {
          const day = getDate(date.date.date)
          const dayOfWeek = date.date.dayOfWeek
          const isHoliday = date.date.holiday
          const disabled = date.date.date < dateTimeToDateText(now).dateText
          const minPrice = Math.min(...date.inventory.map(({ price }) => price))
          const priceVaried = date.inventory.some(({ price }) => price !== minPrice)
          // 過去日付と予約不可の日付はタップ（クリック）できない
          const unClickable = date.inventory.length === 0 || disabled
          return (
            <button
              key={index}
              type="button"
              className={twMerge(
                'flex h-14 flex-col items-center justify-start gap-y-1 pt-1 text-sm leading-none',
                dayOfWeek === 'SUN' || isHoliday ? ' text-peach-dark' : '',
                dayOfWeek === 'SAT' ? 'text-blueberry-dark' : '',
                index === 0 && dayOfWeek === 'MON'
                  ? 'col-start-2'
                  : index === 0 && dayOfWeek === 'TUE'
                    ? 'col-start-3'
                    : index === 0 && dayOfWeek === 'WED'
                      ? 'col-start-4'
                      : index === 0 && dayOfWeek === 'THU'
                        ? 'col-start-5'
                        : index === 0 && dayOfWeek === 'FRI'
                          ? 'col-start-6'
                          : index === 0 && dayOfWeek === 'SAT'
                            ? 'col-start-7'
                            : '',
                selectedDate === date.date.date && 'rounded-md bg-accent-beige',
                disabled && 'justify-center text-neutral-400'
              )}
              onClick={() => handleDateClick(date, disabled)}
              disabled={unClickable}
            >
              {day}
              {disabled ? (
                <></>
              ) : date.inventory.length !== 0 ? (
                <span
                  className={twMerge(
                    'space-y-0.5 text-accent-brown',
                    selectedDate === date.date.date && 'font-bold'
                  )}
                >
                  {isSeatOnly ? (
                    <span className="flex py-2">席のみ</span>
                  ) : (
                    <>
                      <p className="text-sm leading-none">{money(minPrice)}</p>
                      <p className="text-xs leading-none">円{priceVaried ? '〜' : ''}</p>
                    </>
                  )}
                </span>
              ) : (
                <span className="flex size-3 items-center justify-center font-normal">
                  <span className="h-px w-2 bg-neutral-400"></span>
                </span>
              )}
            </button>
          )
        })}
      </div>
    </>
  )
}

export default InventoryCalendar
